import { inject, Injectable } from '@angular/core';
import { EncryptionHandler } from '../constants/encrptionHandler';
import { Subject, BehaviorSubject, Observable, throwError, ReplaySubject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { APP_CONFIG } from '../constants/app.constants';
import { tap, catchError, take, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly http = inject(HttpClient);
  
  // Private properties
  private _userDetails: any;
  public userDetails$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private subscriptionPlan: any;
  private creditLimitDetails: any;
  private $loginStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Completes the bootstrap wait once user/details has settled (or was skipped). */
  private authBootstrapDone$ = new ReplaySubject<void>(1);

  // Public observables
  public _isLoggedIn: Observable<boolean> = this.isLoggedIn$.asObservable();
  public openedLoginAlert: boolean = false;
  public isConsumer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAdmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isSeller: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAgencyEmployee: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isSuperAdmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isGuest: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // Login Status Getter/Setter
  public get isLoggedIn(): Observable<boolean> {
    return this._isLoggedIn;
  }

  public set isLoggedIn(flg: { status: boolean }) {
    this.isLoggedIn$.next(flg.status);
  }

  // User Details Getter/Setter
  public get userDetails() {
    return this._userDetails;
  }

  public set userDetails(data: any) {
    this._userDetails = data;
    this.isConsumer.next(this?.userDetails?.user?.role === 'Consumer');
    this.isAdmin.next(this.userDetails?.user?.details?.agency_role === 'Admin');
    this.isAgencyEmployee.next(this.userDetails?.user?.details?.agency_role === 'Agency Employee');
    this.isSuperAdmin.next(this.userDetails?.user?.details?.agency_role === 'SuperAdmin');
    this.isSeller.next(this.userDetails?.user?.details?.agency_role === 'seller' || this.userDetails?.user?.details?.agency_role === 'Seller');
    if (data?.user) {
      this.isGuest.next(this.isAnonymousGuest(data.user));
    }
    this.userDetails$.next(this.userDetails);
  }

  private isAnonymousGuest(user: any): boolean {
    return !!user?.guest && !user?.verified;
  }

  constructLoginParams(email, password) {
    const params: any = {};

    params['email'] = email;

    const encryptedPW = EncryptionHandler.getCryptData('', 'password', password);
    const origin = window.location.origin;
    params['password'] = origin.indexOf('localhost') > -1 ? password : encryptedPW;

    return params;
  }

  login(email, password) {
    const loginObserver = new Subject();

    let requestHeaderOptions = {};
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    // This bypasses same site attribute for session cookies (keeping this for now without any condition)
    headers = headers.set('mt-client', 'App');
    
    requestHeaderOptions = {
      headers: headers,
      withCredentials: true  // Critical: This ensures cookies are received and sent
    };
    const origin = window.location.origin;
    const url = origin.indexOf('localhost') > -1 ? 'api/auth/api/v1/user/authenticate' : 'api/auth/api/v2/user/authenticate';


    this.http.post(`${APP_CONFIG.url}/${url}`, this.constructLoginParams(email, password), requestHeaderOptions).subscribe({
      next: (res) => {
        loginObserver.next(res);
        this.getUserDetails().subscribe();
      },
      error: (error) => {
        loginObserver.error(error);
      }
    });
    return loginObserver.asObservable();
  }

  // Local Storage Methods
  updateLocalStorage(status: { status: boolean }) {
    localStorage.setItem('logginStatus', JSON.stringify(status));
  }

  addstatus(status: { status: boolean }) {
    localStorage.setItem('logginStatus', JSON.stringify(status));
    this.isLoggedIn = status;
    this.updateLoginStatus(status?.status);
  }

  retrievestatus() {
    const storedStatus = localStorage.getItem('logginStatus');
    const statusValue = storedStatus ? JSON.parse(storedStatus).status : false;
    this.updateLoginStatus(statusValue);
    return storedStatus ? JSON.parse(storedStatus) : { status: false };
  }

  deletestatus() {
    localStorage.removeItem('logginStatus');
  }

  /** Wait until the initial user/details check has completed, then emit current logged-in status. */
  waitForAuth(): Observable<boolean> {
    return this.authBootstrapDone$.pipe(
      take(1),
      switchMap(() => this._isLoggedIn.pipe(take(1)))
    );
  }

  /** Mark the auth bootstrap as complete. Used when user/details is skipped or webconfig fails. */
  markAuthReady(isLoggedIn: boolean): void {
    this.addstatus({ status: isLoggedIn });
    this.authBootstrapDone$.next();
  }

  // User Details Methods
  getUserDetails() {
    return this.http.get(`${APP_CONFIG.url}/api/auth/api/v1/user/details`, { withCredentials: true }).pipe(
      tap((data: any) => {
        const user = data?.user;
        if (user && !this.isAnonymousGuest(user)) {
          this.userDetails = data;
          this.updateLocalStorage({ status: true });
          this.addstatus({ status: true });
          this.isConsumer.next(user?.role === 'Consumer');
          this.isAdmin.next(user?.details?.agency_role === 'admin');
          this.isSeller.next(user?.details?.agency_role === 'seller');
          this.isGuest.next(false);
        } else {
          // Anonymous guest sessions stay logged out in the header.
          this.isGuest.next(!!user?.guest);
          this.userDetails = null;
          this.updateLocalStorage({ status: false });
          this.addstatus({ status: false });
          this.isConsumer.next(false);
          this.isAdmin.next(false);
          this.isSeller.next(false);
        }
        this.authBootstrapDone$.next();
      }),
      catchError((error: HttpErrorResponse) => {
        // Set local storage to `status: false` when there's an error
        this.updateLocalStorage({ status: false });
        this.addstatus({ status: false });
        this.authBootstrapDone$.next();
        return this.handleError(error);
      })
    );
  }

  getUserDetailsAndExtendSession() {
    return this.http.get(`${APP_CONFIG.url}/api/auth/api/v1/user/details?extend_session=true`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  removeUserInfo() {
    delete this._userDetails;
  }

  retrieveUserRole() {
    return this._userDetails?.user?.role;
  }

  // Login Status Observable Methods
  public updateLoginStatus(status: boolean): void {
    this.$loginStatus.next(status);
  }

  public getLoginStatus(): Observable<boolean> {
    return this.$loginStatus.asObservable();
  }

  // Logout Method
  logout() {
    const logoutObserver = new Subject();
    let requestHeaderOptions = {};
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    requestHeaderOptions = {
      headers: headers,
      withCredentials: true  // Critical: This ensures cookies are sent with the request
    };

    this.http.get(`${APP_CONFIG.url}/api/auth/api/v1/user/logout`, requestHeaderOptions).subscribe({
      next: (res: any) => {
        this.addstatus({ status: false });
        this.removeUserInfo();
        this.isGuest.next(false);
        localStorage.removeItem('user_ui_id');
        logoutObserver.next(res);
      },
      error: (error) => {
        // Even if logout API fails, clear local state
        // This ensures user appears logged out even if backend call fails
        this.addstatus({ status: false });
        this.removeUserInfo();
        this.isGuest.next(false);
        localStorage.removeItem('user_ui_id');
        logoutObserver.error(error);
      }
    });
    return logoutObserver.asObservable();
  }

  // Subscription and Agency Methods
  public getAgencyDetails(agencyId: string): void {
    const url = `${APP_CONFIG.url}/api/auth/api/v1/subscription/${agencyId}`;
    this.http.get(url, { withCredentials: true }).subscribe(
      (data: any) => {
        this.subscriptionPlan = data.subscription?.plan_name;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  public getSubscriptionPlan(): string {
    return this.subscriptionPlan;
  }

  // Credit Limit Methods
  public getWalletCreditDetails() {
    const credit$ = new Subject();
    this.http.get(`${APP_CONFIG.url}/api/auth/api/v1/wallet/credit`, { withCredentials: true }).subscribe(
      (data: any) => {
        this.creditLimitDetails = data;
        credit$.next(data);
      },
      (error) => {
        console.error('Error', error);
        this.creditLimitDetails = null;
        credit$.error(error);
      }
    );
    return credit$.asObservable();
  }

  retrieveCreditLimit() {
    return this.creditLimitDetails;
  }

  // Error Handling
  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('Unable to make the request', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
      if (error.status === 401 || error.status === 400) {
        return throwError(() => new Error('Unauthorized Error'));
      }
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  // Register user
  constructRegisterParams(formDetails) {
    const params = {
        email: formDetails.email,
        role: formDetails.role,
        guest: formDetails.guest,
        register_and_continue: formDetails.register_and_continue,
        details: {
            first_name: formDetails.firstName,
            last_name: formDetails.lastName,
            title: formDetails.title,
            mobile_number: formDetails.mobileNumber,
            code: formDetails.code,
        },
    };

    return params;
}
  register(formDetails, isOtpFlow?) {
    const registerObserver = new Subject();
    let requestHeaderOptions = {};
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    //this is to bypass same site attribute for mobile session so reprice
    //works
    if (window.location.origin.indexOf('localhost') > -1) {
      headers = headers.set('mt-client', 'App');
    }
    requestHeaderOptions = {
        headers: headers,
        withCredentials: true
    };

    this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/create`, this.constructRegisterParams(formDetails), requestHeaderOptions).subscribe((res) => {
      registerObserver.next(res);
    }, (error) => {
      registerObserver.error(error);
    });
    return registerObserver.asObservable();
  }

  updateProfile(formDetails) {
    const updateObserver = new Subject();
    let requestHeaderOptions = {};
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    requestHeaderOptions = {
        headers: headers,
        withCredentials: true  // Critical: This ensures cookies are sent with the request
    };

    this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/update`, this.constructRegisterParams(formDetails), requestHeaderOptions).subscribe({
      next: (res) => {
        updateObserver.next(res);
        updateObserver.complete();
      },
      error: (error) => {
        updateObserver.error(error);
      }
    });
    return updateObserver.asObservable();
  }

  // Forgot Password Methods
  constructForgotPasswordParams(email: string) {
    return {
      email: email
    };
  }

  forgotPassword(email: string) {
    const forgotObserver = new Subject();
    let requestHeaderOptions = {};
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    // This bypasses same site attribute for session cookies (keeping this for now without any condition)
    headers = headers.set('mt-client', 'App');
    
    requestHeaderOptions = {
      headers: headers,
      withCredentials: true  // Critical: This ensures cookies are sent with the request
    };

    this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/forgot-password`, this.constructForgotPasswordParams(email), requestHeaderOptions).subscribe({
      next: (res) => {
        forgotObserver.next(res);
      },
      error: (error) => {
        forgotObserver.error(error);
      }
    });
    return forgotObserver.asObservable();
  }

  setPassword(payload: any) {
    return this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/password/set`, payload, { withCredentials: true })
      .pipe(
        catchError(this.handleError)
      );
  }

  verifyToken(payload: any): Observable<any> {
    return this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/verify`, payload, { withCredentials: true })
  }

  resendLink(payload: any): Observable<any> {
    return this.http.post(`${APP_CONFIG.url}/api/auth/api/v1/user/create/resend`, payload, { withCredentials: true })
      .pipe(
        catchError(this.handleError)
      );
  }
}
